import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Copy, CheckCircle, WandSparkles } from "lucide-react";
import { cn } from "@/lib/utils";

type MarketingRewriteSuggestionsDemoProps = {
  className?: string;
};

const demoRewrites = [
  {
    approach: "Keep it punchy",
    rewrite: "Arcitext helps every writer stay on-brand. Reduced feedback loops, more time to create.",
    reasoning: "Highlights the value in a concise, confident tone for social captions.",
  },
  {
    approach: "Proof-first",
    rewrite: "Teams using Arcitext publish 4x faster because of reduced feedback loops.",
    reasoning: "Adds a metric-led hook that works for landing pages and sales decks.",
  },
  {
    approach: "Story-driven",
    rewrite: "Imagine publishing every launch with the confidence that your brand will sound like you. Arcitext makes that the baseline.",
    reasoning: "Leans into narrative copy for newsletters or webinar promos.",
  },
];

export const MarketingRewriteSuggestionsDemo = ({ className }: MarketingRewriteSuggestionsDemoProps) => {
  return (
    <Card className={cn("max-w-lg", className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <WandSparkles className="h-4 w-4" />
          Rewrite suggestions
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <Tabs defaultValue="approach-0" className="flex flex-col gap-3 w-full">
          <TabsList className="flex flex-col items-start h-auto gap-1 p-1">
            {demoRewrites.map((suggestion, index) => (
              <TabsTrigger
                key={suggestion.approach}
                value={`approach-${index}`}
                className="text-xs whitespace-normal text-left h-auto py-2 px-2 leading-tight w-full justify-start"
              >
                {suggestion.approach}
              </TabsTrigger>
            ))}
          </TabsList>

          {demoRewrites.map((suggestion, index) => (
            <TabsContent key={suggestion.approach} value={`approach-${index}`}>
              <div className="rounded-lg border border-border/70 bg-background/80 p-4 space-y-3">
                <p className="text-sm font-medium text-foreground">{suggestion.rewrite}</p>
                <p className="text-xs text-muted-foreground">{suggestion.reasoning}</p>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Copy className="h-3.5 w-3.5" />
                    Copy rewrite
                  </Button>
                  <Button size="sm" className="gap-2">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Use this
                  </Button>
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default MarketingRewriteSuggestionsDemo;
